import { Badge } from 'components/badge';
import { getCartAmounts, isCartOrCheckout } from 'utils/helpers';
import { BadgeColor, BadgePadding, PaymentMethodType } from 'types';

import { Result, TotalCartProps } from './types';

export const getTotalCardData = ({
  tip,
  data,
  balance,
  shippingPrice,
  paymentMethods,
  companyProcessingFees,
  isProcessingFeeEnabled,
}: Omit<TotalCartProps, 'children'>) => {
  const { tax, saleTotal, totalAmount, processingFees } = getCartAmounts({
    tip,
    data,
    shippingPrice,
    paymentMethods,
    isProcessingFeeEnabled,
  });

  const isFreeShipping = isCartOrCheckout(data)
    ? data?.shipping?.isFreeShipping
    : data?.isFreeShipping;

  const result: Result[] = [
    { title: 'Sub Total', value: totalAmount },
    { value: tax, title: `Tax (${data?.event?.tax ?? 0}%)` },
  ];

  if (typeof tip === 'number') {
    result.push({
      value: tip,
      title: 'Tip',
    });
  }

  if (isProcessingFeeEnabled) {
    result.push({
      value: +processingFees,
      title: `Processing Fees (${companyProcessingFees}%)`,
    });
  }

  if (Number(shippingPrice) > 0) {
    result.push({
      title: 'Shipping',
      value: isFreeShipping ? 0 : shippingPrice,
      subtitle: isFreeShipping ? (
        <Badge
          text="Free"
          styleType={BadgeColor.Blue}
          padding={BadgePadding.Small}
        />
      ) : undefined,
    });
  }

  result.push({ value: saleTotal, title: 'Sale Total' });

  if (typeof balance === 'number') {
    result.push({ value: balance, title: 'Balance Due' });
  }

  if (paymentMethods?.some((method) => method.receivedCashAmount)) {
    const cashPayments = paymentMethods.filter(
      (method) => method.type === PaymentMethodType.Cash
    );

    const totalReceivedCashAmount = cashPayments.reduce(
      (acc, payment) => acc + (payment.receivedCashAmount ?? 0),
      0
    );

    const totalCashAmount = cashPayments.reduce(
      (acc, payment) => acc + +payment.amount,
      0
    );

    const changeAmount = Math.max(
      Number((totalReceivedCashAmount - totalCashAmount).toFixed(2)),
      0
    );

    if (changeAmount > 0) {
      result.push({ title: 'Change', value: changeAmount });
    }
  }

  return result;
};
